import type { InternalAxiosRequestConfig } from "axios";

// Every backend call says who is acting: the X-DBR-Actor header carries the
// name of the team member picked in the user selector. The backend records it
// in the audit log and in quote_sent_by (backend-node/src/middleware/actor.js).
// It is attribution only - it changes nothing about what a request may do.

// Written by UserContext (useLocalStorage) as JSON of a TeamMember.
const ACTOR_KEY = "selectedSignoffUser";
const ACTOR_MAX_LENGTH = 64;

/** The selected team member's name, made header-safe; null when none. */
function actorName(): string | null {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(ACTOR_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { name?: unknown } | null;
    const name = typeof parsed?.name === "string" ? parsed.name : "";
    // Header values must be printable ASCII; the server applies the same rule.
    const clean = name.replace(/[^\x20-\x7e]/g, "").trim().slice(0, ACTOR_MAX_LENGTH);
    return clean || null;
  } catch {
    return null;
  }
}

/** `{ 'X-DBR-Actor': name }`, or `{}` when no user is picked. */
export function actorHeaders(): Record<string, string> {
  const actor = actorName();
  return actor ? { "X-DBR-Actor": actor } : {};
}

/**
 * Axios request interceptor. Read per request, not at client creation:
 * apiClient is built at module load, before anyone picks a user.
 */
export function attachActor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  for (const [key, value] of Object.entries(actorHeaders())) {
    config.headers.set(key, value);
  }
  return config;
}

/** fetch() for a backend URL, with the actor header added. */
export function fetchWithActor(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [key, value] of Object.entries(actorHeaders())) headers.set(key, value);
  return fetch(input, { ...init, headers });
}
